import { useChooseEmailTemplate } from 'modules/email/hooks/use-choose-email-template'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import {
  AttachmentFileInterface,
  EmailErrorType,
  MailingApiInterface,
  UnsavedAttachmentFileInterface,
} from 'shared/components/dashboard-mail-editor/types/email-interface'
import { TempStateType } from 'shared/components/dashboard-mail-editor/types/temp-state-type'
import { dispatchSkipShowConfirmationExitOnceEvent } from 'shared/confirmation-modal-before-exit/event'
import { getEmailEditorUrl } from 'shared/utils/get-email-editor-url'

interface NewsletterStateProps<T extends EmailErrorType> {
  mailing: MailingApiInterface
  onSave: (tempState: TempStateType) => Promise<void>
  defaultErrors: T
}

const setDefaultState = (mailing: MailingApiInterface) => {
  const currentEmail = mailing.currentEmail
  const { id, body, html, fromEmail, fromName, subject, previewText, useNewEditor, attachments } =
    currentEmail
  return {
    id,
    body,
    html,
    fromEmail,
    fromName,
    subject,
    previewText,
    useNewEditor,
    attachments,
  }
}

export const useMailingState = <T extends EmailErrorType>({
  onSave,
  mailing,
  defaultErrors,
}: NewsletterStateProps<T>) => {
  const { push } = useRouter()

  const [tempState, setTempState] = useState<TempStateType>(setDefaultState(mailing))

  const [attachmentsTempState, setAttachmentsTempState] = useState<
    (AttachmentFileInterface | UnsavedAttachmentFileInterface)[]
  >(mailing.currentEmail.attachments)

  const [errors, setErrors] = useState<T>(defaultErrors)

  const setDefaultErrors = () => setErrors(defaultErrors)

  const hasErrors = Object.values(errors).some(
    value => typeof value === 'string' && value.length > 0,
  )

  useEffect(() => {
    setTempState(setDefaultState(mailing))
    setAttachmentsTempState(mailing.currentEmail.attachments)
  }, [mailing])

  const { fetcherChooseEmailTemplate, isFetchingChooseEmailTemplate } = useChooseEmailTemplate()
  const onChangeEmail = async (newTempState: TempStateType) => {
    if (newTempState.useNewEditor !== tempState.useNewEditor) {
      try {
        if (newTempState.useNewEditor && !tempState.html && newTempState.template) {
          const { html } = await fetcherChooseEmailTemplate(mailing.currentEmail.id, {
            emailTemplate: newTempState.template,
          })
          await onSave({ ...newTempState, html })
        } else {
          await onSave(newTempState)
        }
        if (newTempState.useNewEditor) {
          dispatchSkipShowConfirmationExitOnceEvent()
          push(getEmailEditorUrl(mailing.id))
        }
      } catch (e) {}
    } else {
      setTempState(newTempState)
    }
  }

  return {
    mailing,
    tempState,
    setTempState,
    attachmentsTempState,
    setAttachmentsTempState,
    onChangeEmail,
    isFetchingChooseEmailTemplate,
    errors,
    setErrors,
    setDefaultErrors,
    hasErrors,
  }
}
